import {computed, ref} from "vue";
import {defineStore} from 'pinia'


import {AcknowledgeData, AlertData, DialogData} from "@/interfaces/common";
import {User} from "@/interfaces/users";
import {loadWhoAmIData} from "@/http/myUserApi";

// You can name the return value of `defineStore()` anything you want,
// but it's best to use the name of the store and surround it with `use`
// and `Store` (e.g. `useUserStore`, `useCartStore`, `useProductStore`)
// the first argument is a unique id of the store across your application
export const useMainStore = defineStore('main', () => {
    /*
	In Setup Stores:

	ref()s become state properties
	computed()s become getters
	function()s become actions
	 */

    // STATE
    const modalMask = ref<boolean>(false);
    const modalLogin = ref<boolean>(false);
    const modalTerms = ref<boolean>(false);
    const modalCookies = ref<boolean>(localStorage.getItem('cookies-accepted') == null);
    const alertid = ref<number>(1);
    const alertsList = ref<AlertData[]>([]);
    const dialogData = ref<DialogData>({message: ''});
    const acknowledgeData = ref<AcknowledgeData>({message: ''});
    const loggedUser = ref<User | null>(null);

    // GETTERS
    const isShowMask = computed<boolean>(() => modalMask.value);
    const isShowLogin = computed<boolean>(() => modalLogin.value);
    const isShowTerms = computed<boolean>(() => modalTerms.value);
    const isShowCookies = computed<boolean>(() => modalCookies.value);
    const getAlerts = computed<AlertData[]>(() => alertsList.value);
    const getDialog = computed<DialogData>(() => dialogData.value);
    const getAcknowledge = computed<AcknowledgeData>(() => acknowledgeData.value);
    const getLoggedUser = computed<User | null>(() => loggedUser.value);
    const isAdmin = computed<boolean>(() => !loggedUser.value || !loggedUser.value.profile ? false : loggedUser.value.profile.name === 'ROLE_ADMIN')
    const isContributor = computed<boolean>(() => !loggedUser.value || !loggedUser.value.profile ? false : loggedUser.value.profile.name === 'ROLE_CONTRIBUTOR')
    const isEconomist = computed<boolean>(() => !loggedUser.value || !loggedUser.value.profile ? false : loggedUser.value.profile.name === 'ROLE_ECONOMIST')

    // ACTIONS
    function showMask(value: boolean) {
        modalMask.value = value;
    }

    function showLogin(value: boolean) {
        modalLogin.value = value;
    }

    function showTerms(value: boolean) {
        modalTerms.value = value;
    }

    function showCookies(value: boolean) {
        modalCookies.value = value;
    }

    // success | error | warning | info
    function showAlert(alert: AlertData) {
        alert.id = ++alertid.value;
        alertsList.value.push(alert);
        setTimeout(() => {
            alertsList.value.shift();
        }, 5000)// 5000 = 5s
    }

    function showDialog(data?: DialogData | undefined) {
        if (data) {
            dialogData.value.title = data.title ?? 'Are you sure?'
            dialogData.value.btnStyle = data.btnStyle ?? 'btn-primary';
            dialogData.value.message = data.message
            dialogData.value.type = data.type
            dialogData.value.okText = data.okText
            dialogData.value.successCallback = data.successCallback ? data.successCallback : undefined
            dialogData.value.cancelText = data.cancelText
            dialogData.value.cancelCallback = data.cancelCallback ? data.cancelCallback : undefined
            dialogData.value.show = true
        } else {
            dialogData.value.show = false
        }
    }

    function showAcknowledge(data?: AcknowledgeData) {
        if (data) {
            acknowledgeData.value.title = data.title
            acknowledgeData.value.message = data.message
            acknowledgeData.value.btnStyle = data.btnStyle
            acknowledgeData.value.type = data.type
            acknowledgeData.value.show = true
        } else {
            acknowledgeData.value.show = false
        }
    }


    function loadWhoAmI() {
        return loadWhoAmIData()
            .then(response => {
                // console.debug('Who Am I response: ', response)
                // console.debug('Who Am I response: ', typeof response.data)
                if (response.data && (typeof response.data) === 'object') {
                    // console.debug('setting Who Am I response: ', response.data)
                    loggedUser.value = response.data;
                } else {
                    // console.debug('Who Am I response not found!')
                    loggedUser.value = null;
                }
                return response
            })
            .catch(error => {
                // console.error('error loading logged user ', error)
                loggedUser.value = null;
                throw error;
            })
    }

    return {
        isShowMask,
        showMask,

        isShowLogin,
        showLogin,

        isShowTerms,
        showTerms,

        isShowCookies,
        showCookies,

        getAlerts,
        showAlert,

        getDialog,
        showDialog,

        getAcknowledge,
        showAcknowledge,

        getLoggedUser,
        loadWhoAmI,

        isAdmin,
        isContributor,
        isEconomist,

    }

})
